// =============================================
// DIALOG DE GESTIÓN DE CONTRASEÑA
// Para que admin establezca/resetee contraseñas
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Chip,
  Stack,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import passwordService from '../../services/passwordService';

const PasswordDialog = ({ open, onClose, user, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [strength, setStrength] = useState(null);
  const [checkingStrength, setCheckingStrength] = useState(false);

  // Limpiar al abrir/cerrar
  useEffect(() => {
    if (open) {
      setPassword('');
      setShowPassword(false);
      setSendEmail(true);
      setError('');
      setSuccess('');
      setStrength(null);
    }
  }, [open]);

  // Verificar fuerza de contraseña
  useEffect(() => {
    if (password.length > 0) {
      const timer = setTimeout(async () => {
        try {
          setCheckingStrength(true);
          const result = await passwordService.checkStrength(password);
          setStrength(result);
        } catch (err) {
          console.error('Error checking strength:', err);
        } finally {
          setCheckingStrength(false);
        }
      }, 500); // Debounce 500ms

      return () => clearTimeout(timer);
    } else {
      setStrength(null);
    }
  }, [password]);

  // Generar contraseña automática
  const handleGeneratePassword = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const result = await passwordService.generateTemporary(user.id, false);
      
      setPassword(result.temporary_password);
      setSuccess('Contraseña generada correctamente');
      setShowPassword(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al generar contraseña');
    } finally {
      setLoading(false);
    }
  };

  // Establecer contraseña
  const handleSetPassword = async () => {
    if (!password) {
      setError('Ingrese una contraseña');
      return;
    }

    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const result = await passwordService.setPassword(
        user.id,
        password,
        sendEmail
      );

      setSuccess(
        `Contraseña establecida correctamente${
          result.email_sent ? ' y enviada por email' : ''
        }`
      );

      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al establecer contraseña');
    } finally {
      setLoading(false);
    }
  };

  // Resetear contraseña
  const handleResetPassword = async () => {
    if (!user.email) {
      setError('Usuario no tiene email registrado');
      return;
    }

    if (!window.confirm(`¿Resetear contraseña de ${user.name}? Se enviará nueva contraseña por email.`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const result = await passwordService.resetPassword(user.id);

      setSuccess('Contraseña restablecida y enviada por email');

      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al resetear contraseña');
    } finally {
      setLoading(false);
    }
  };

  // Copiar contraseña
  const handleCopyPassword = () => {
    navigator.clipboard.writeText(password);
    setSuccess('Contraseña copiada al portapapeles');
    setTimeout(() => setSuccess(''), 2000);
  };

  // Color de fuerza de contraseña
  const getStrengthColor = () => {
    if (!strength) return 'default';
    const strengthMap = {
      'Muy fuerte': 'success',
      'Fuerte': 'success',
      'Media': 'warning',
      'Débil': 'error',
      'Muy débil': 'error'
    };
    return strengthMap[strength.strength] || 'default';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        🔑 Gestionar Contraseña
        <Typography variant="body2" color="text.secondary">
          {user?.name} - Casa #{user?.house_number}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Campo de contraseña */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Nueva Contraseña"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            placeholder="Mínimo 8 caracteres"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                  {password && (
                    <IconButton onClick={handleCopyPassword} edge="end">
                      <CopyIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />

          {/* Indicador de fuerza */}
          {strength && (
            <Box sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={strength.strength}
                  color={getStrengthColor()}
                  size="small"
                />
                {checkingStrength && <CircularProgress size={16} />}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {strength.checks.length && '✓ Al menos 8 caracteres'}
                {strength.checks.uppercase && ' • ✓ Mayúsculas'}
                {strength.checks.lowercase && ' • ✓ Minúsculas'}
                {strength.checks.number && ' • ✓ Números'}
                {strength.checks.symbol && ' • ✓ Símbolos'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Botón generar */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleGeneratePassword}
          disabled={loading}
          sx={{ mb: 2 }}
        >
          Generar Contraseña Automática
        </Button>

        {/* Checkbox enviar email */}
        <FormControlLabel
          control={
            <Checkbox
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              disabled={loading || !user?.email}
            />
          }
          label={
            <Typography variant="body2">
              Enviar contraseña por email
              {!user?.email && (
                <Typography variant="caption" color="error" display="block">
                  (Usuario sin email)
                </Typography>
              )}
            </Typography>
          }
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Cancelar
        </Button>

        {user?.email && (
          <Button
            onClick={handleResetPassword}
            disabled={loading}
            color="warning"
            variant="outlined"
          >
            {loading ? <CircularProgress size={24} /> : 'Resetear y Enviar'}
          </Button>
        )}

        <Button
          onClick={handleSetPassword}
          disabled={loading || !password}
          variant="contained"
        >
          {loading ? <CircularProgress size={24} /> : 'Establecer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordDialog;
